import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Flame, Users, ShieldAlert, Siren, Save, RotateCcw } from "lucide-react";

const SECTIONS = [
  { key: "member", title: "Member chat heat", icon: Flame, desc: "Fast messages, repeats, mentions, links, invites, files, emoji/caps spam. Overheating steps up: warn → timeout → kick → ban.",
    fields: [["threshold", "Overheat at"], ["decay_per_sec", "Cools per second"], ["w_message", "Per message"], ["w_duplicate", "Repeated message"], ["w_mention", "Per mention"], ["w_mass_mention", "@everyone / @here"], ["w_link", "Link"], ["w_invite", "Invite link"], ["w_attachment", "Per file"], ["w_emoji_caps", "Emoji / caps spam"], ["timeout_minutes", "Timeout (min)"], ["strike_window_hours", "Strikes reset after (h)"]] },
  { key: "join", title: "Server join heat", icon: Users, desc: "Every join heats the server; brand-new and no-avatar accounts add more. Overheating locks the server until it cools.",
    fields: [["threshold", "Panic at"], ["decay_per_sec", "Cools per second"], ["w_join", "Per join"], ["w_new_account", "New account bonus"], ["w_no_avatar", "No avatar bonus"], ["new_account_days", "New = younger than (days)"], ["cool_threshold", "Unlock below"], ["panic_min_minutes", "Min lockdown (min)"]],
    toggles: [["kick_new_during_panic", "Kick new accounts during panic"]] },
  { key: "nuke", title: "Staff / bot nuke heat", icon: ShieldAlert, desc: "Admins or bots deleting channels/roles or mass banning heat up fast. Overheating strips their roles (bots are kicked) and DMs the owner.",
    fields: [["threshold", "Quarantine at"], ["decay_per_sec", "Cools per second"], ["w_channel_delete", "Channel delete"], ["w_role_delete", "Role delete"], ["w_ban", "Ban"], ["w_kick", "Kick"], ["w_channel_create", "Channel create"], ["w_role_create", "Role create"], ["w_webhook", "Webhook change"]] },
];

const heatColor = (p) => (p >= 80 ? "#EF4444" : p >= 50 ? "#F59E0B" : p >= 20 ? "#FBBF24" : "#10B981");

const Gauge = ({ label, value, max, testid }) => {
  const pct = Math.min(100, Math.round(((value || 0) / (max || 100)) * 100));
  return (
    <div className="bg-[#141622] border border-white/5 p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-xs text-[#94A3B8] mb-2"><span>{label}</span><span className="font-mono" style={{ color: heatColor(pct) }}>{Math.round(value || 0)} / {max}</span></div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, #10B981, ${heatColor(pct)})`, boxShadow: `0 0 12px ${heatColor(pct)}` }} />
      </div>
    </div>
  );
};

const Toggle = ({ on, onClick, testid, disabled }) => (
  <button role="switch" aria-checked={on} data-testid={testid} disabled={disabled} onClick={onClick}
    className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-[#F97316]" : "bg-white/10"}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
  </button>
);

const SectionCard = ({ s, cfg, setSec }) => (
  <div className="bg-[#0D0E15] border border-white/10 p-5 space-y-4" data-testid={`heat-section-${s.key}`}>
    <div className="flex items-start gap-3">
      <s.icon className="w-5 h-5 text-[#F97316] mt-0.5" />
      <div className="flex-1">
        <div className="font-display font-semibold">{s.title}</div>
        <div className="text-xs text-[#94A3B8] mt-1 leading-relaxed">{s.desc}</div>
      </div>
      <Toggle on={cfg.enabled} onClick={() => setSec(s.key, "enabled", !cfg.enabled)} testid={`heat-${s.key}-toggle`} />
    </div>
    <div className="grid grid-cols-2 gap-2">
      {s.fields.map(([k, label]) => (
        <label key={k} className="text-[11px] text-[#64748B] space-y-1">
          <span>{label}</span>
          <input type="number" step="any" data-testid={`heat-${s.key}-${k}`} value={cfg[k]} onChange={e => setSec(s.key, k, e.target.value)}
            className="w-full px-2 py-1.5 text-sm font-mono border" />
        </label>
      ))}
    </div>
    {(s.toggles || []).map(([k, label]) => (
      <div key={k} className="flex items-center justify-between text-xs text-[#94A3B8]">
        <span>{label}</span>
        <Toggle on={!!cfg[k]} onClick={() => setSec(s.key, k, !cfg[k])} testid={`heat-${s.key}-${k}`} />
      </div>
    ))}
  </div>
);

export const HeatSystem = ({ guildId }) => {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback((withCfg) => api.get(`/guilds/${guildId}/heat`).then(r => { setData(r.data); if (withCfg) setCfg(r.data.config); }).catch(() => {}), [guildId]);
  useEffect(() => { load(true); const id = setInterval(() => load(false), 5000); return () => clearInterval(id); }, [load]);

  const save = async (next = cfg) => {
    setSaving(true);
    try { const r = await api.put(`/guilds/${guildId}/heat`, next); setCfg(r.data.config); toast.success("Heat system saved"); load(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const setSec = (sec, k, v) => setCfg(c => ({ ...c, [sec]: { ...c[sec], [k]: v } }));
  const panic = async (enabled) => {
    try { await api.post(`/guilds/${guildId}/heat/panic`, { enabled }); toast.success(enabled ? "Panic lockdown on" : "Lockdown lifted"); load(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data || !cfg) return <div className="font-mono text-sm text-[#8B5CF6]">// loading heat system…</div>;
  const live = data.live, inPanic = !!data.state?.panic;
  return (
    <div className="space-y-4" data-testid="heat-system">
      <div className="bg-[#0D0E15] border border-[#F97316]/25 p-5 flex flex-wrap items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center"><Flame className="w-5 h-5 text-[#F97316]" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold text-xl">Heat System</div>
          <div className="text-xs text-[#94A3B8]">Risky actions add heat that cools over time. Too hot = automatic punishment. Admins and trusted roles are never heated.</div>
        </div>
        <span data-testid="heat-enabled-state" className={`font-mono text-xs ${cfg.enabled ? "text-[#F97316]" : "text-[#64748B]"}`}>{cfg.enabled ? "ARMED" : "OFF"}</span>
        <Toggle on={cfg.enabled} disabled={saving} testid="heat-master-toggle" onClick={() => save({ ...cfg, enabled: !cfg.enabled })} />
      </div>

      {inPanic && (
        <div className="flex flex-wrap items-center gap-3 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-[14px] p-4" data-testid="heat-panic-banner">
          <Siren className="w-5 h-5 text-[#EF4444] animate-pulse" />
          <div className="flex-1 text-sm"><span className="font-semibold text-[#EF4444]">Panic lockdown active</span> <span className="text-[#94A3B8]">since {new Date(data.state.since).toLocaleTimeString()} · {data.state.why}. Lifts automatically when the server cools.</span></div>
          <button data-testid="heat-end-panic" onClick={() => panic(false)} className="px-3 py-1.5 text-xs border border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444] hover:text-white">End lockdown now</button>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <Gauge label="Server join heat" value={live.server_heat} max={data.config.join.threshold} testid="heat-gauge-server" />
        <Gauge label={`Hottest member${live.members[0] ? ` · ${live.members[0].user_id}` : ""}`} value={live.members[0]?.heat} max={data.config.member.threshold} testid="heat-gauge-member" />
        <Gauge label={`Hottest staff/bot${live.staff[0] ? ` · ${live.staff[0].user_id}` : ""}`} value={live.staff[0]?.heat} max={data.config.nuke.threshold} testid="heat-gauge-staff" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {SECTIONS.map(s => <SectionCard key={s.key} s={s} cfg={cfg[s.key]} setSec={setSec} />)}
      </div>

      <div className="bg-[#0D0E15] border border-white/10 p-5 grid md:grid-cols-3 gap-3">
        {[["trusted_user_ids", "Trusted staff/bot IDs (never nuke-heated)"], ["exempt_role_ids", "Exempt role IDs (no chat heat)"], ["exempt_channel_ids", "Exempt channel IDs (no chat heat)"]].map(([k, label]) => (
          <label key={k} className="text-[11px] text-[#64748B] space-y-1">
            <span>{label}</span>
            <input data-testid={`heat-${k}`} value={Array.isArray(cfg[k]) ? cfg[k].join(", ") : cfg[k] || ""} placeholder="comma-separated IDs"
              onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}
              className="w-full px-2 py-1.5 text-xs font-mono border" />
          </label>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="heat-save" disabled={saving} onClick={() => save()} className="flex items-center gap-2 px-5 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold disabled:opacity-50"><Save className="w-4 h-4" /> Save heat settings</button>
        <button data-testid="heat-defaults" onClick={() => setCfg({ ...data.defaults, enabled: cfg.enabled })} className="flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-white/30 text-sm text-[#94A3B8]"><RotateCcw className="w-4 h-4" /> Defaults</button>
        {!inPanic && <button data-testid="heat-start-panic" onClick={() => panic(true)} className="ml-auto flex items-center gap-2 px-4 py-2 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444] hover:text-white text-sm"><Siren className="w-4 h-4" /> Panic lockdown now</button>}
      </div>

      <div className="bg-[#0D0E15] border border-white/10 p-5" data-testid="heat-events">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-3">Recent heat events · {data.events.length}</div>
        {data.events.length === 0 ? <div className="text-xs text-[#64748B]">Nothing has overheated yet.</div> : data.events.map(e => (
          <div key={e.id} className="flex items-center gap-3 text-xs py-1.5 border-b border-white/5">
            <span className="font-mono uppercase w-16 text-[#F97316]">{e.kind}</span>
            <span className="text-[#E2E8F0] truncate flex-1">{e.detail}</span>
            {e.action && <span className="px-1.5 py-0.5 rounded-md bg-[#EF4444]/15 text-[#EF4444] font-mono text-[10px] uppercase">{e.action}</span>}
            <span className="font-mono text-[10px] text-[#64748B] whitespace-nowrap">{new Date(e.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
